import React from "react";
import OrderDTO from "@/dto/order/OrderDTO";
import OrderSellerRemarksDTO from "@/dto/order/OrderSellerRemarksDTO";
import ShopType from "@/enum/ShopType";
import { ModalStore } from "@/hooks/Modal/ModalStore";
import BatchUpdateDropShippingSellerRemarksDialog, {
    UpdateDropShippingSellerRemarksModalProps,
} from "@/scenes/OrderManagement/components/dialogs/BatchUpdateDropShippingSellerRemarksDialog";
import BatchUpdateSellerRemarksDialog, {
    BatchUpdateSellerRemarksDialogProps,
} from "@/scenes/OrderManagement/components/dialogs/BatchUpdateSellerRemarksDialog";
import TbBatchUpdateSellerRemarksModal, {
    TbBatchUpdateSellerRemarksModalProps,
} from "@/scenes/OrderManagement/components/dialogs/TbBatchUpdateSellerRemarksModal";
import GlobalStore from "@/store";
import { Modal } from "antd";

/**
 * 检查订单中平台类型分布情况
 */
export const analyzePlatformDistribution = (orders: OrderDTO[], globalStore: typeof GlobalStore) => {
    let containsTaobaoOrders = false;
    let containsNonTaobaoOrders = false;
    let containSupplierOrder = false;

    for (const order of orders) {
        if (containsTaobaoOrders && containsNonTaobaoOrders && containSupplierOrder) break;

        const shop = globalStore.shopMapping.get(order.shopId);

        if (shop?.type === ShopType.TAOBAO) {
            containsTaobaoOrders = true;
        } else {
            containsNonTaobaoOrders = true;

            if (order.supplierDropShippingInfo) {
                containSupplierOrder = true;
            }
        }
    }

    return { containsTaobaoOrders, containsNonTaobaoOrders, containSupplierOrder };
};

const openTbBatchModal = (modalStore: ModalStore, props: TbBatchUpdateSellerRemarksModalProps) => {
    modalStore.openModal(TbBatchUpdateSellerRemarksModal, props);
};

const openCommonBatchModal = (modalStore: ModalStore, props: BatchUpdateSellerRemarksDialogProps) => {
    modalStore.openModal(BatchUpdateSellerRemarksDialog, props);
};

const openBatchUpdateDropShippingSellerRemarksModal = (
    modalStore: ModalStore,
    props: UpdateDropShippingSellerRemarksModalProps,
) => {
    modalStore.openModal(BatchUpdateDropShippingSellerRemarksDialog, props);
};

const showMixedPlatformError = () => {
    Modal.error({
        title: "淘宝订单不支持和其他平台订单一起修改备注",
        content: "请仅选择淘宝订单批量修改备注，或者仅选择非淘宝订单批量修改备注",
    });
};

interface BatchUpdateOptions {
    shopId?: number;
    orders: OrderDTO[];
    modalStore: ModalStore;
    globalStore: typeof GlobalStore;
    onUpdateOrderSellerRemarks?: (orderId: number, sellerRemarks: OrderSellerRemarksDTO) => void;
    onUpdateSupplierOrderRemarks?: (supplierOrderId: number, supplierRemarks: string) => void;
}

const openBatchUpdateSellerRemarksModal = (options: BatchUpdateOptions) => {
    const {
        orders,
        modalStore,
        onUpdateOrderSellerRemarks,
        globalStore,
        shopId,
        onUpdateSupplierOrderRemarks,
    } = options;

    // 检查订单是否包含淘宝平台和其他平台订单
    const { containsTaobaoOrders, containsNonTaobaoOrders, containSupplierOrder } = analyzePlatformDistribution(
        orders,
        globalStore,
    );

    if (containSupplierOrder) {
        if (containsTaobaoOrders) {
            showMixedPlatformError();
        } else {
            /** 既有代发订单又有其他平台订单，就打开通用修改备注（弹窗内再分组调用修改备注和代发备注的接口） */
            const supplierOrderCount = orders.filter((order) => order.supplierDropShippingInfo).length;
            const platformOrderCount = orders.length - supplierOrderCount;

            if (platformOrderCount) {
                Modal.confirm({
                    title: <div></div>,
                    content: (
                        <div style={{ position: "relative", top: "-8px" }}>
                            <p>当前选中{platformOrderCount}个平台订单，可修改卖家备注</p>
                            <p>当前选中{supplierOrderCount}个供货商订单，可修改代发备注</p>
                            <p>同时修改平台订单与供货商订单的备注时，旗帜仅对卖家备注生效</p>
                        </div>
                    ),
                    okCancel: true,
                    okText: "去修改备注",
                    onOk() {
                        openCommonBatchModal(modalStore, {
                            onUpdateOrderSellerRemarks,
                            orders,
                            onUpdateSupplierOrderRemarks,
                        });
                    },
                });
            } else {
                /** 只有代发订单就打开修改代发备注 */
                openBatchUpdateDropShippingSellerRemarksModal(modalStore, { orders, onUpdateSupplierOrderRemarks });
            }
        }

        return;
    }

    // 根据平台类型执行不同逻辑
    if (containsTaobaoOrders && !containsNonTaobaoOrders) {
        openTbBatchModal(modalStore, { onUpdateOrderSellerRemarks, orders, shopId });
    } else if (containsTaobaoOrders && containsNonTaobaoOrders) {
        showMixedPlatformError();
    } else {
        openCommonBatchModal(modalStore, {
            onUpdateOrderSellerRemarks,
            orders,
            onUpdateSupplierOrderRemarks,
        });
    }
};

export default openBatchUpdateSellerRemarksModal;
